/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES DEUX CHEMINS DE CONTACT, ET LEURS DEUX PRÉREQUIS OUVERTS — F-72.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-72 : « **deux chemins, et aucun des deux ne fait entrer quoi que ce soit dans le
 * produit.** » Le premier est un bloc embarqué d'une autre plateforme du porteur ; le second
 * est une adresse email du groupe affichée sur le site.
 *
 * **CES DEUX VALEURS VIVENT DANS `shared/` ET NON DANS LA FEATURE DE CONTACT, ET LA RAISON
 * EST UNE FRONTIÈRE DE COUCHE.** L'adresse email est affichée **par le pied de page**, donc
 * sur les huit pages (F-69 : « le pied de page porte le chemin vers la page juridique, la
 * page du groupe et **l'adresse email affichée** »). Le pied est une primitive, et
 * `site/CLAUDE.md` ferme le chemin : « **`shared/` ne remonte jamais vers une feature** ».
 *
 * Ce sont d'ailleurs **des constantes de compilation et non des règles métier** : ce que la
 * feature `contact` porte, elle, est une règle — le consentement dû par le bloc tiers.
 */

/**
 * **L'adresse du formulaire embarqué, gravée au build. PRÉREQUIS 21, OUVERT.**
 *
 * F-72 : « le premier est un formulaire de contact, et un seul. C'est un **bloc embarqué
 * généré par une autre plateforme du porteur** : Swype ne l'a pas écrit et ne le sert pas,
 * **il l'inclut**. »
 *
 * `CDC-6` §8, prérequis 21 : « **obtenir le code embarqué du formulaire de contact** auprès
 * de la plateforme qui le génère », et il bloque « **la page 5 seule** ». Il est **non fait**
 * au 20260825.
 *
 * **Elle vient d'une variable de build, jamais d'un littéral** — même règle que l'adresse de
 * l'API —, et **la politique de sécurité de contenu la dérive de la même variable** : deux
 * endroits qui nomment une origine divergent au premier changement d'environnement.
 */
export const ADRESSE_DU_FORMULAIRE: string | undefined = import.meta.env
  .VITE_CONTACT_IFRAME_URL;

/**
 * **L'adresse email du groupe affichée sur le site. PRÉREQUIS 22, OUVERT.**
 *
 * F-72 : « le second chemin est une adresse email du groupe affichée sur le site. La personne
 * écrit **depuis sa propre boîte** vers celle du groupe : **le produit n'est sur aucun des
 * deux bouts.** »
 *
 * **LE COÛT EST ASSUMÉ ET ÉCRIT AU CAHIER DES CHARGES**, et c'est ce qui décide de la forme
 * de l'affichage : « une adresse email affichée sur une page publique est **récoltée et
 * reçoit du courrier non sollicité**. C'est le prix de son affichage, **il est accepté**. »
 * Elle n'est donc **pas obfusquée** — ni en image, ni en JavaScript, ni en entités HTML : ces
 * parades gênent les lecteurs d'écran, ne trompent plus personne, et **contourneraient un
 * coût que le corpus a explicitement accepté**.
 *
 * **Prérequis 22, non fait au 20260825** : « **aucune valeur d'adresse ici** : le nom de la
 * boîte se pose au versement ». Tant qu'elle n'est pas posée, **le site n'invente pas une
 * adresse** — afficher une adresse qui ne reçoit rien est pire que de n'en afficher aucune :
 * §13 — « quelqu'un écrit à l'adresse affichée et personne ne lit la boîte → **le produit
 * n'en sait rien** », et la personne non plus.
 */
export const ADRESSE_EMAIL: string | undefined = import.meta.env
  .VITE_EMAIL_CONTACT;

/** Vrai quand le formulaire embarqué est posé — prérequis 21. */
export function leFormulaireEstOuvert(): boolean {
  return ADRESSE_DU_FORMULAIRE !== undefined && ADRESSE_DU_FORMULAIRE !== '';
}

/** Vrai quand l'adresse du groupe est posée — prérequis 22. */
export function lAdresseEstOuverte(): boolean {
  return ADRESSE_EMAIL !== undefined && ADRESSE_EMAIL !== '';
}
